import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import apiClient from '../../api/client';
import Badge from '../../components/ui/badge';
import { Button } from '../../components/ui/button';

// Platform health — staff-only surface (backend 403s everyone else).
// Data: /api/platform/health/ (service heartbeats, per-tenant sync staleness,
// send counters, celery queues + task failures, alert states).

const fmtTime = (v) => {
  if (!v) return '—';
  try { return new Date(v).toLocaleString(); } catch { return v; }
};

const STATUS_COLOR = { up: 'emerald', down: 'red', degraded: 'amber', unknown: 'gray' };

function Section({ title, children, right }) {
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">{title}</h3>
        {right}
      </div>
      {children}
    </div>
  );
}

export default function PlatformHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    apiClient.get('/platform/health/')
      .then(r => { setData(r.data); setForbidden(false); })
      .catch(e => {
        if (e?.response?.status === 403) setForbidden(true);
        else toast.error('Failed to load platform health');
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000); // live-ish
    return () => clearInterval(t);
  }, [load]);

  if (forbidden) {
    return <p className="p-8 text-center text-gray-500">Platform staff only.</p>;
  }
  if (loading && !data) {
    return <p className="p-8 text-center text-gray-500">Loading platform health…</p>;
  }
  if (!data) return null;

  const queues = data.celery?.queues || {};
  const queueRows = typeof queues === 'object' && !queues.error ? Object.entries(queues) : [];

  // sends: collapse to per tenant+channel+source totals over the window
  const sendTotals = {};
  for (const r of data.sends || []) {
    const k = `${r.tenant}|${r.channel}|${r.source}`;
    if (!sendTotals[k]) sendTotals[k] = { ...r, attempted: 0, delivered: 0, failed: 0 };
    sendTotals[k].attempted += r.attempted;
    sendTotals[k].delivered += r.delivered;
    sendTotals[k].failed += r.failed;
  }
  const sendRows = Object.values(sendTotals).sort((a, b) => b.attempted - a.attempted);

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-gray-900 dark:text-white">Platform Health</h2>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-400">as of {fmtTime(data.generated_at)}</span>
          <Button size="sm" variant="outline" onClick={load} loading={loading}>Refresh</Button>
        </div>
      </div>

      {/* Services */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {(data.services || []).map(s => (
          <div key={s.service} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold">{s.service}</span>
              <Badge color={STATUS_COLOR[s.status] || 'gray'}>{s.status}</Badge>
            </div>
            <div className="mt-1 text-[11px] text-gray-500 space-y-0.5">
              <div>{s.http_status ? `HTTP ${s.http_status}` : '—'}{s.latency_ms != null ? ` · ${s.latency_ms}ms` : ''}</div>
              <div>24h uptime: {s.uptime_24h_pct != null ? `${s.uptime_24h_pct}%` : '—'} ({s.checks_24h} checks)</div>
              <div>last: {fmtTime(s.last_check)}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Sync staleness */}
      <Section title="FieldRoutes sync — per tenant (stale > 6h alarms)">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-gray-500 border-b border-gray-200 dark:border-gray-700">
              <th className="py-1.5 pr-3">Tenant</th>
              <th className="py-1.5 pr-3 text-right">Endpoints</th>
              <th className="py-1.5 pr-3">Last success</th>
              <th className="py-1.5 pr-3 text-right">Hours since</th>
              <th className="py-1.5 pr-3 text-right">Failed runs (24h)</th>
              <th className="py-1.5">State</th>
            </tr></thead>
            <tbody>
              {(data.sync || []).map(r => (
                <tr key={r.tenant_id} className="border-b border-gray-100 dark:border-gray-800">
                  <td className="py-1.5 pr-3">{r.tenant}</td>
                  <td className="py-1.5 pr-3 text-right">{r.endpoints}</td>
                  <td className="py-1.5 pr-3 text-xs">{fmtTime(r.last_success)}</td>
                  <td className="py-1.5 pr-3 text-right">{r.hours_since_success ?? '—'}</td>
                  <td className={`py-1.5 pr-3 text-right ${r.failed_runs_24h ? 'text-red-500 font-medium' : ''}`}>{r.failed_runs_24h}</td>
                  <td className="py-1.5"><Badge color={r.stale ? 'red' : 'emerald'}>{r.stale ? 'STALE' : 'fresh'}</Badge></td>
                </tr>
              ))}
              {!(data.sync || []).length && <tr><td colSpan={6} className="py-3 text-center text-gray-400">No sync checkpoints</td></tr>}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Send pipeline */}
      <Section title="Send pipeline — last 7 days (attempted / delivered / failed)">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-gray-500 border-b border-gray-200 dark:border-gray-700">
              <th className="py-1.5 pr-3">Tenant</th>
              <th className="py-1.5 pr-3">Channel</th>
              <th className="py-1.5 pr-3">Source</th>
              <th className="py-1.5 pr-3 text-right">Attempted</th>
              <th className="py-1.5 pr-3 text-right">Delivered</th>
              <th className="py-1.5 pr-3 text-right">Failed</th>
              <th className="py-1.5 text-right">Failure %</th>
            </tr></thead>
            <tbody>
              {sendRows.map((r, i) => {
                const pct = r.attempted ? Math.round(100 * r.failed / r.attempted) : 0;
                return (
                  <tr key={i} className="border-b border-gray-100 dark:border-gray-800">
                    <td className="py-1.5 pr-3">{r.tenant}</td>
                    <td className="py-1.5 pr-3">{r.channel}</td>
                    <td className="py-1.5 pr-3 text-xs text-gray-500">{r.source}</td>
                    <td className="py-1.5 pr-3 text-right">{r.attempted}</td>
                    <td className="py-1.5 pr-3 text-right">{r.delivered}</td>
                    <td className={`py-1.5 pr-3 text-right ${r.failed ? 'text-red-500' : ''}`}>{r.failed}</td>
                    <td className={`py-1.5 text-right ${pct > 25 ? 'text-red-500 font-semibold' : 'text-gray-500'}`}>{pct}%</td>
                  </tr>
                );
              })}
              {!sendRows.length && <tr><td colSpan={7} className="py-3 text-center text-gray-400">No sends in window</td></tr>}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Celery */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Section title="Celery queues" right={
          <span className="text-xs text-gray-400">{data.celery?.failures_24h ?? 0} task failures (24h)</span>
        }>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-gray-500 border-b border-gray-200 dark:border-gray-700">
              <th className="py-1.5 pr-3">Queue</th>
              <th className="py-1.5 pr-3 text-right">Depth</th>
              <th className="py-1.5">Consumer</th>
            </tr></thead>
            <tbody>
              {queueRows.map(([q, info]) => (
                <tr key={q} className="border-b border-gray-100 dark:border-gray-800">
                  <td className="py-1.5 pr-3">{q}</td>
                  <td className={`py-1.5 pr-3 text-right ${info.depth > 100 ? 'text-red-500 font-medium' : ''}`}>{info.depth}</td>
                  <td className="py-1.5">
                    {info.consumed
                      ? <Badge color="emerald">consumed</Badge>
                      : <Badge color={info.depth > 0 ? 'red' : 'amber'}>NO CONSUMER</Badge>}
                  </td>
                </tr>
              ))}
              {queues.error && <tr><td colSpan={3} className="py-2 text-red-500 text-xs">broker error: {queues.error}</td></tr>}
            </tbody>
          </table>
        </Section>

        <Section title="Recent task failures (signal-captured)">
          <div className="space-y-1.5 max-h-64 overflow-y-auto">
            {(data.celery?.recent_failures || []).map((f, i) => (
              <div key={i} className="text-xs border-b border-gray-100 dark:border-gray-800 pb-1.5">
                <span className="font-medium">{f.task}</span>
                <span className="text-red-500 ml-2">{f.exc}</span>
                <span className="text-gray-400 ml-2">{fmtTime(f.at)}</span>
                <div className="text-gray-500 truncate">{f.message}</div>
              </div>
            ))}
            {!(data.celery?.recent_failures || []).length && (
              <p className="text-xs text-gray-400">No captured failures — the signal handler reports every one.</p>
            )}
          </div>
        </Section>
      </div>

      {/* Alert states */}
      <Section title="Alert states (dedup ledger — active red, resolved gray)">
        <div className="flex flex-wrap gap-2">
          {(data.alerts || []).map(a => (
            <span key={a.key} className="text-xs flex items-center gap-1">
              <Badge color={a.active ? 'red' : 'gray'}>{a.key}</Badge>
              <span className="text-gray-400">{a.last_sent ? `emailed ${fmtTime(a.last_sent)}` : 'not yet emailed'}</span>
            </span>
          ))}
          {!(data.alerts || []).length && <p className="text-xs text-gray-400">No alerts recorded yet.</p>}
        </div>
      </Section>
    </div>
  );
}
